import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import uuid from "uuid";

import {
  gql,
  GraphQLAPI,
  GraphQLResult,
  queries,
  typedGql,
  typedGqlMutation,
  GraphQLQuery,
} from "@examspark/api";
import { collectAllPages, exists } from "@examspark/common";
import {
  Classroom,
  Course,
  CreateAcademicCalendarInput,
  CreateCourseCalendarInput,
  IDSK,
  SearchableCourseCalendarConnection,
  SearchableResourceConnectionNew,
  UpdateAcademicCalendarInput,
  UpdateCourseCalendarInput,
  CourseCalendar,
  SearchableConnection,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { usePaginatedSearch } from "../../hooks/usePaginatedSearch";
import { synthesizeCourseCalendar } from "../../utils/util";

import { useQueryGetCourse, useQueryGetCourseUnits } from "./course";
import { useQueryGetWorkspace } from "./workspace";

export const useMutationDeleteAcademicCalendar = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const result = await gql.operations.workspace.deleteAcademicCalendar(
        params.id,
        authorizationContext,
      );
      return result;
    },
  });
};

export const useMutationDeleteCourseCalendar = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (params: { key: IDSK }) => {
      const result = await gql.operations.workspace.deleteCourseCalendar(
        params.key,
        authorizationContext,
      );
      return result;
    },
  });
};

export const searchCourseCalendarsQueryFn =
  queries.calendar.searchCourseCalendars({
    label: "SearchCourseCalendars",
    fields: `
    id
    sk
    schemaVersion
    workspaceId
    maturity
    bankId
    visibility
    name
    isArchived
    calendarId
    calendar {
      id
      sk
      workspaceId
      schemaVersion
      name
      startAt
      endAt
      instructionWeekdays
      isDeleted
      nonInstructionDates {
        type
        label
        startAt
        endAt
      }
      gradingPeriodLength
      gradingPeriods {
        label
        startAt
        endAt
      }
    }
    courseId
    course {
      id
      sk
      schemaVersion
      workspaceId
      name
    }
    units {
      id
      dates
    }
    additionalNonInstructionDates {
      type
      label
      startAt
      endAt
    }
    tags {
      key
      type
      value
    }
  `,
  });

export const useQuerySearchCourseCalendarsByCourse = (courseId?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course-calendars", courseId],
    queryFn: async () => {
      const result = await GraphQLAPI.query({
        query: searchCourseCalendarsQueryFn,
        variables: {
          limit: 50,
          authorizationContext,
          nextToken: "",
          filter: {
            sk: courseId
              ? {
                  eq: `course-calendar#${courseId}#v0`,
                }
              : {
                  wildcard: `course-calendar#*`,
                },
            isDeleted: {
              ne: true,
            },
          },
        },
      });

      return result;
    },
  });
};

export const usePaginatedSearchCourseCalendarsByCourse = (
  courseId?: string,
  name?: string,
  includeArchived = false,
) => {
  const authorizationContext = useAuthorizationContext();

  return usePaginatedSearch({
    queryKey: ["course-calendars", courseId, name, includeArchived],
    queryFn: async (context) => {
      const filter: SearchableResourceFilterInput = {
        sk: courseId
          ? {
              eq: `course-calendar#${courseId}#v0`,
            }
          : {
              wildcard: `course-calendar#*`,
            },
        isDeleted: {
          ne: true,
        },
      };

      if (name) {
        filter.and = name.split(" ").map((n) => ({
          name: {
            matchPhrasePrefix: `${n}`,
          },
        }));
      }

      if (!includeArchived) {
        filter.isArchived = {
          ne: true,
        };
      }
      const result = (await GraphQLAPI.query({
        query: searchCourseCalendarsQueryFn,
        variables: {
          authorizationContext,
          filter,
          ...(context.meta?.variables ?? {}),
        },
      })) as GraphQLResult<{
        searchCourseCalendars: SearchableConnection<CourseCalendar>;
      }>;

      return result.data?.searchCourseCalendars;
    },
  });
};

export const useQueryGetClassroomCourseCalendar = (
  classroom: Classroom | undefined,
) => {
  const authorizationContext = useAuthorizationContext();

  const { data: course } = useQueryGetCourse(
    classroom?.course?.id ?? undefined,
  );
  const { data: courseUnits } = useQueryGetCourseUnits(
    classroom?.course?.id ?? undefined,
  );

  return useQuery({
    queryKey: ["calendar", classroom?.calendarId, classroom?.course?.id],
    enabled: !!classroom && courseUnits != null,
    queryFn: async () => {
      const [id, sk] = classroom?.calendarId?.split("/") ?? [];

      if (!id || !sk || !courseUnits || !course) return null;

      const query = sk.startsWith("course-calendar")
        ? typedGql(queries.classroom.getCourseCalendar)
        : typedGql(queries.classroom.getAcademicCalendar);

      const res = await query({
        input: {
          id,
          sk,
        },
        authorizationContext,
      });

      if (res?.data == null) {
        return null;
      }

      // type shenanigans to maintain our improved types from the typedGql function across both synthetic and course calendars
      type FullType = typeof res.data;
      type TypedCalendarReturn = NonNullable<
        FullType extends GraphQLQuery<infer I>
          ? I extends { getCourseCalendar: infer R }
            ? R
            : never
          : never
      >;

      if ("getCourseCalendar" in res.data) {
        return res.data.getCourseCalendar;
      }

      const ac = res.data.getAcademicCalendar;

      if (!ac) {
        return null;
      }

      // we need the tags from unitIds but the filtering from the query
      const unitIds = course?.unitIds?.filter(({ id }) =>
        courseUnits?.find((unit) => unit.id === id),
      );

      const fakeCourse = {
        ...course,
        unitIds,
      };

      const courseCalenderAlike: TypedCalendarReturn = synthesizeCourseCalendar(
        fakeCourse,
        ac,
      );

      return courseCalenderAlike;
    },
  });
};

export const useQueryListAcademicCalendars = (
  workspaceId?: string,
  options?: Partial<{
    includeArchived?: boolean;
  }>,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["academic-calendars", workspaceId, options?.includeArchived],
    queryFn: async () => {
      const calendarIds = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.search.search(),
          variables: {
            limit: 50,
            authorizationContext,
            nextToken,
            filter: {
              sk: {
                eq: `academic-calendar#v0`,
              },
              isDeleted: {
                ne: true,
              },
              ...(!options?.includeArchived
                ? {
                    isArchived: {
                      ne: true,
                    },
                  }
                : {}),
            },
          },
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        return result.data?.search;
      });

      return (
        await Promise.all(
          calendarIds
            .flatMap((r) => r.items ?? [])
            .map(async (r) => {
              const calendarData = await typedGql(
                queries.classroom.getAcademicCalendar,
              )({
                input: {
                  id: r.id,
                  sk: r.sk,
                },
                authorizationContext,
              });

              return calendarData.data?.getAcademicCalendar;
            }),
        )
      ).filter(exists);
    },
  });
};

export const useQueryListCourseCalendarsByCourse = (
  course: Course | undefined,
  options?: Partial<{
    includeArchived?: boolean;
  }>,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course-calendars", course?.id],
    queryFn: async () => {
      const results = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.calendar.searchCourseCalendars({
            fields: `
              id
              sk
              schemaVersion
              workspaceId
              maturity
              bankId
              visibility
              isArchived
              name
              calendarId
              calendar {
                id
                sk
                workspaceId
                schemaVersion
                name
                startAt
                endAt
                instructionWeekdays
                isDeleted
                nonInstructionDates {
                  type
                  label
                  startAt
                  endAt
                }
                gradingPeriodLength
                gradingPeriods {
                  label
                  startAt
                  endAt
                }
              }
              courseId
              course {
                id
                sk
                schemaVersion
                workspaceId
                name
              }
              units {
                id
                dates
              }
              additionalNonInstructionDates {
                type
                label
                startAt
                endAt
              }
            `,
          }),
          variables: {
            limit: 50,
            authorizationContext,
            nextToken,
            filter: {
              sk: course?.id
                ? {
                    eq: `course-calendar#${course.id}#v0`,
                  }
                : {
                    wildcard: `course-calendar#*`,
                  },
              isDeleted: {
                ne: true,
              },
              ...(options?.includeArchived
                ? {}
                : {
                    isArchived: {
                      ne: true,
                    },
                  }),
            },
          },
        })) as GraphQLResult<{
          searchCourseCalendars: SearchableCourseCalendarConnection;
        }>;

        return result.data?.searchCourseCalendars;
      });

      return results.flatMap((r) => r.items ?? []);
    },
  });
};

export const useQueryGetAcademicCalendar = ({
  input,
}: {
  input?: {
    id: string;
    sk: "academic-calendar#v0";
  };
}) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    queryKey: ["academic-calendar", input?.id],
    enabled: !!input?.id,
    queryFn: async () => {
      if (!input?.id) return;

      return typedGql(queries.classroom.getAcademicCalendar)({
        input,
        authorizationContext,
      }).then((r) => r.data?.getAcademicCalendar);
    },
  });
};

export const useQueryGetDefaultWorkspaceCalendar = (workspaceId?: string) => {
  const { data: workspace, refetch: refetchWorkspace } =
    useQueryGetWorkspace(workspaceId);

  const id = workspace?.tags?.find(
    (t) => t?.key === "default-calendar-id",
  )?.value;

  const query = useQueryGetAcademicCalendar({
    ...(id
      ? {
          input: { id, sk: "academic-calendar#v0" },
        }
      : {}),
  });

  return {
    ...query,
    refetch: async () => {
      await refetchWorkspace();
      return query.refetch();
    },
  };
};

export const useQueryGetCourseCalendar = (
  courseCalendarId: string | undefined,
  courseId: string | undefined,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course-calendar", courseId, courseCalendarId],
    enabled: !!courseCalendarId && !!courseId,
    queryFn: async () => {
      if (!courseCalendarId || !courseId) return null;

      const res = await typedGql(queries.classroom.getCourseCalendar)({
        input: {
          id: courseCalendarId,
          sk: `course-calendar#${courseId}#v0`,
        },
        authorizationContext,
      });

      return res.data?.getCourseCalendar ?? null;
    },
  });
};

export const useQueryGetAllCalendarsForCourse = (
  course: Course | undefined,
  params?: Partial<{
    includeArchivedCalendars?: boolean;
  }>,
) => {
  const authorizationContext = useAuthorizationContext();

  const courseCalendars = useQueryListCourseCalendarsByCourse(course, {
    includeArchived: params?.includeArchivedCalendars ?? false,
  });
  const academicCalendars = useQueryListAcademicCalendars(
    authorizationContext.workspaceId ?? undefined,
    {
      includeArchived: params?.includeArchivedCalendars ?? false,
    },
  );

  const courseUnits = useQueryGetCourseUnits(course?.id).data;

  return useQuery({
    queryKey: [
      "course",
      course?.id,
      "all-calendars",
      params?.includeArchivedCalendars,
    ],
    enabled:
      !!course?.id &&
      courseCalendars.data != null &&
      academicCalendars.data != null &&
      courseUnits != null,
    queryFn: async () => {
      // fetch all course calendars that apply fetch all academic calendars and
      // filter to those that DO NOT already have a course calendar bootstrap
      // course calendar-looking records for each academic calendar

      if (!course || courseUnits == null) return null;

      const unitIds = course.unitIds?.filter((unit) =>
        courseUnits.find((cu) => cu.id === unit.id),
      );
      const fakeCourse = {
        ...course,
        unitIds,
      };

      const unmatchedAcademicCalendars = academicCalendars.data?.filter(
        (ac) => {
          return !courseCalendars.data?.find((cc) => cc.calendarId === ac.id);
        },
      );

      // make the fake calendars look like the return type of the course calendar query
      type TypedCalendarReturn = NonNullable<
        typeof courseCalendars.data
      >[number];

      const academicCalendarAsCourseCalendars = unmatchedAcademicCalendars?.map(
        (ac) => {
          const courseCalendarAlike: TypedCalendarReturn =
            synthesizeCourseCalendar(fakeCourse, ac);

          return courseCalendarAlike;
        },
      );

      return [
        ...(courseCalendars.data ?? []),
        ...(academicCalendarAsCourseCalendars ?? []),
      ];
    },
  });
};

export const useMutationCreateCourseCalendar = () => {
  const authorizationContext = useAuthorizationContext();
  return useMutation({
    mutationFn: async (input: Omit<CreateCourseCalendarInput, "id" | "sk">) => {
      const result = typedGqlMutation(queries.classroom.createCourseCalendar)({
        authorizationContext,
        input: {
          ...input,
          id: uuid(),
          sk: ["course-calendar", input.courseId, "v0"].join("#"),
          maturity: "OPERATIONAL",
          type: "COURSE_CALENDAR",
        },
      });

      return result;
    },
  });
};

export const useMutationCreateAcademicCalendar = () => {
  const authorizationContext = useAuthorizationContext();
  return useMutation({
    mutationFn: async (
      input: Omit<CreateAcademicCalendarInput, "id" | "sk">,
    ) => {
      const result = typedGqlMutation(queries.classroom.createAcademicCalendar)(
        {
          authorizationContext,
          input: {
            ...input,
            id: uuid(),
            sk: "academic-calendar#v0",
          },
        },
      );

      return result;
    },
  });
};

export const useMutationUpdateCourseCalendar = () => {
  const authorizationContext = useAuthorizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateCourseCalendarInput) => {
      const result = await typedGqlMutation(
        queries.classroom.updateCourseCalendar,
      )({
        authorizationContext,
        input,
      });

      queryClient.invalidateQueries(["course-calendars"]);
      queryClient.invalidateQueries(["course-calendar"]);

      return result;
    },
  });
};

export const useMutationUpdateAcademicCalendar = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (input: Omit<UpdateAcademicCalendarInput, "sk">) => {
      const result = typedGqlMutation(queries.classroom.updateAcademicCalendar)(
        {
          authorizationContext,
          input: {
            ...input,
            sk: "academic-calendar#v0",
          },
        },
      );

      return result;
    },
  });
};

export const useMutationSetDefaultWorkspaceCalendar = () => {
  const authorizationContext = useAuthorizationContext();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      sk: "workspace";
      defaultAcademicCalendarId: string;
    }) => {
      const result = typedGqlMutation(
        queries.classroom.setDefaultWorkspaceCalendar,
      )({
        authorizationContext,
        input: {
          id: input.id,
          sk: input.sk,
          defaultCalendarTag: {
            type: "string",
            key: "default-calendar-id",
            value: input.defaultAcademicCalendarId,
          },
        },
      });

      return result;
    },
  });
};
